import { Injectable } from '@angular/core';
import { SELECTORS } from '../utils/constants';

@Injectable()
export class CursorManagementService {
  /**
   * Obtém o texto dentro de um nó container antes da posição atual do cursor.
   */
  public getTextBeforeCursor(container: Node, range: Range): string {
    const preCursorRange = range.cloneRange();
    preCursorRange.selectNodeContents(container);
    preCursorRange.setEnd(range.startContainer, range.startOffset);
    return preCursorRange.toString();
  }

  /**
   * Lida com a tecla Backspace para remover chips e blocos vazios.
   */
  public handleBackspace(
    event: KeyboardEvent,
    editorElement: HTMLElement,
    onModified: () => void,
  ): void {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    const { startContainer, startOffset } = range;
    const container = (
      startContainer.nodeType === Node.ELEMENT_NODE ? startContainer : startContainer.parentElement
    ) as HTMLElement;

    if (
      (container.matches(SELECTORS.BLOCK_PROGRAM) || container.matches(SELECTORS.BLOCK_INVERSE)) &&
      startOffset === 0
    ) {
      const content = container.textContent ?? '';
      if (content === '' || content === '\u200B' || container.innerHTML.toLowerCase() === '<br>') {
        const blockStatement = container.closest(SELECTORS.BLOCK_STATEMENT);
        if (blockStatement) {
          event.preventDefault();
          blockStatement.remove();
          onModified();
          return;
        }
      }
    }

    if (startContainer.nodeType === Node.ELEMENT_NODE && startOffset > 0) {
      const nodeBefore = startContainer.childNodes[startOffset - 1];
      if (nodeBefore?.nodeType === Node.ELEMENT_NODE) {
        const elementBefore = nodeBefore as HTMLElement;
        const isChipOrBlock =
          elementBefore.matches(SELECTORS.CHIP_WRAPPER) ||
          elementBefore.matches(SELECTORS.BLOCK_STATEMENT);
        if (elementBefore.getAttribute('contenteditable') === 'false' && isChipOrBlock) {
          event.preventDefault();
          elementBefore.remove();
          onModified();
        }
      }
    }
  }

  /**
   * Posiciona o cursor no final do conteúdo do elemento.
   */
  public setCursorAtEnd(element: HTMLElement): void {
    const selection = window.getSelection()!;
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /**
   * Foca no primeiro argumento editável de um bloco ou função.
   */
  public focusFirstArgument(container: Element): void {
    const focusTarget =
      container.querySelector<HTMLElement>(`${SELECTORS.ARG_NAME}${SELECTORS.CONTENT_EDITABLE}`) ??
      container.querySelector<HTMLElement>(SELECTORS.BLOCK_PROGRAM);

    if (focusTarget) {
      const selection = window.getSelection()!;
      const range = document.createRange();
      range.selectNodeContents(focusTarget);
      if (
        focusTarget.classList.contains('is-placeholder') ||
        focusTarget.classList.contains('block-statement__program')
      ) {
        range.collapse(true);
      }

      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  /**
   * Move o foco para o próximo argumento disponível.
   */
  public focusNextArgument(currentArg: HTMLElement): boolean {
    const allArgs = Array.from(
      currentArg
        .closest(`[data-function-name], ${SELECTORS.BLOCK_HEADER}`)!
        .querySelectorAll(SELECTORS.ARG_NAME),
    );
    const currentIndex = allArgs.indexOf(currentArg);
    const nextArg = allArgs[currentIndex + 1] as HTMLElement | undefined;

    if (nextArg) {
      const selection = window.getSelection()!;
      const range = document.createRange();
      range.selectNodeContents(nextArg);
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    }

    return false;
  }

  /**
   * Move o cursor para depois de um elemento específico.
   */
  public setCursorAfterElement(element: Element): void {
    const selection = window.getSelection()!;
    const range = document.createRange();
    range.setStartAfter(element);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}
